import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { PencilIcon, PlusIcon } from "@heroicons/react/outline";
import Table from "../../../../Components/v2/Table";
import FormError from "../../../../Components/v2/Form/FormError";
import DateTimeCell from "../../../../Components/v2/Table/DateTimeCell";
import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import AccountingPageHeader from "../../../../Components/v2/Accounting/AccountingPageHeader";
import Stats from "../../../../Components/v2/Stats";
import {
  addLocalNotification,
  clearAccountingTransaction,
  clearAcctTransactionError,
  requestDeleteAcctTransaction,
  requestGetExpenseStats,
  requestListAccountingTransaction,
  requestUpdateAcctTransaction,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
  useNotificationDispatch,
  usePeriodLock,
} from "../../../../Context";
import PeriodLockBanner from "../../../../Components/v2/Accounting/PeriodLockBanner";
import PeriodLockCell from "../../../../Components/v2/Accounting/PeriodLockCell";
import Pagination from "../../../../Components/v2/Pagination";
import {
  cancelAPIRequest,
  convertToTitleCase,
  formatCurrency,
  getErrorMessages,
  getHasPaymentsMessage,
  getPaymentCount,
} from "../../../../Utils";
import TableActions from "../../../../Components/v2/Table/TableActions";
import { isStockAdjustmentJournal } from "../../../../Utils/AccountingUtil";
import TableHeader from "../../../../Components/v2/Table/TableHeader";
import { ModalAlert } from "../../../../Components/ModalAlert";
import Modal from "../../../../Components/v2/Modal";
import TransactionModal from "../../../../Components/v2/Accounting/TransactionModal";
import TransactionDetailModal from "../../../../Components/v2/Accounting/TransactionDetailModal";
import InvoiceModal from "../../../../Components/v2/Accounting/InvoiceModal";
import InvoiceDetailModal from "../../../../Components/v2/Accounting/InvoiceDetailModal";
import ExpandableDescription from "../../../../Components/v2/Accounting/ExpandableDescription";
import FormInput from "../../../../Components/v2/Form/FormInput";
import Button from "../../../../Components/v2/Button";
import { useInvoiceLink } from "../../../../Hooks/useInvoiceLink";
import { wibStatsMonths, wibThisMonth } from "../../../../Utils/PeriodUtils";
import ErrorDialog from "../../../../Components/v2/ErrorDialog";

const AccountingExpenses = () => {
  const { t, i18n } = useTranslation();

  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [isTransactionDetailModalOpen, setIsTransactionDetailModalOpen] =
    useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isInvoiceDetailModalOpen, setIsInvoiceDetailModalOpen] =
    useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isEditAmountModalOpen, setIsEditAmountModalOpen] = useState(false);
  const [editingValue, setEditingValue] = useState("");
  const [editingError, setEditingError] = useState(null);

  const accountingDispatch = useAccountingDispatch();
  const notificationDispatch = useNotificationDispatch();

  const {
    request,
    transactions,
    loading: expenseLoading,
    error: expenseError,
    nextTokens,
    deletedID,
    submitError,
    submitLoading,
    expenseStats,
    expenseStatsLoading,
    expenseStatsError,
    transactionID,
  } = useAccountingState();
  const { user } = useAuthState();
  const { isDateClosed } = usePeriodLock(user?.mainBusiness?.id);

  // Reports link here to open one bill.
  useInvoiceLink({
    onOpen: (invoice) => {
      setSelectedInvoice(invoice);
      setIsInvoiceDetailModalOpen(true);
    },
  });

  const filter = useMemo(
    () => ({
      or: [{ type: { eq: "EXPENSE" } }, { type: { eq: "EXPENSE_PAYABLE" } }],
    }),
    [],
  );

  const statsData = useMemo(() => {
    const { current, previous, top } = expenseStats || {};
    const totalThisMonth = current?.total || 0;
    const highestTransaction = top?.transaction || {};
    const highestAccount = top?.account || {};

    const calculatePercentageChange = (current, previous) => {
      const currentValue = typeof current === "number" ? current : 0;
      const previousValue = typeof previous === "number" ? previous : 0;

      // Handle the case where previousValue is 0
      if (previousValue === 0) {
        return {
          change: currentValue,
          direction: currentValue > 0 ? "up" : "no-change",
          color: currentValue > 0 ? "green" : "gray",
        };
      }

      const change = ((currentValue - previousValue) / previousValue) * 100;

      // Check for no change
      if (change === 0) {
        return {
          change: 0,
          direction: "no-change",
          color: "gray",
        };
      }

      return {
        change: change.toFixed(2) + "%",
        direction: change >= 0 ? "up" : "down",
        color: change >= 0 ? "green" : "red",
      };
    };

    const percentageChangeData = calculatePercentageChange(
      current?.total,
      previous?.total,
    );

    return [
      {
        title: t("expenses.stats.labels.totalThisMonth"),
        value: totalThisMonth,
        footer:
          percentageChangeData.direction === "no-change"
            ? t("expenses.stats.labels.noChange")
            : `${
                typeof percentageChangeData.change === "number"
                  ? new Intl.NumberFormat(i18n.language, {
                      notation: "compact",
                      compactDisplay: "short",
                    }).format(percentageChangeData.change)
                  : percentageChangeData.change
              } ${
                percentageChangeData.direction === "up"
                  ? t("expenses.stats.labels.higher")
                  : t("expenses.stats.labels.lower")
              }`,
        trend: {
          direction: percentageChangeData.direction,
          color:
            percentageChangeData.direction === "no-change"
              ? "gray"
              : percentageChangeData.direction === "up"
                ? "red"
                : "green",
        },
      },
      {
        title: t("expenses.stats.labels.highestTransaction"),
        value: highestTransaction.total || 0,
        footer: highestTransaction.invoiceNumber ? (
          <a
            href={`/invoice/${highestTransaction.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            Invoice #{highestTransaction.invoiceNumber}
          </a>
        ) : highestTransaction.id ? (
          <a
            href={`/accounting-transaction/create?id=${highestTransaction.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            {t("expenses.stats.labels.transaction")}{" "}
            {highestTransaction.id?.slice(-5).toUpperCase()}
          </a>
        ) : (
          t("common.noData")
        ),
      },
      {
        title: t("expenses.stats.labels.highestExpense"),
        value: highestAccount.total || 0,
        footer: highestAccount.name || t("common.noData"),
      },
    ];
  }, [expenseStats, t, i18n.language]);

  // Clear errors on unmount
  useEffect(() => {
    return () => {
      if (request) {
        cancelAPIRequest(request);
      }
      clearAccountingTransaction(accountingDispatch);
      clearAcctTransactionError(accountingDispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user.mainBusiness?.id) {
      requestListAccountingTransaction(accountingDispatch, {
        page: 1,
        limit: perPage,
        businessID: user.mainBusiness.id,
        createdAt: {
          between: [
            wibThisMonth().startDate,
            wibThisMonth().endDate,
          ],
        },
        filter,
        // Payment rows are dropped before the page is
        // counted, so a page can no longer come back empty.
        excludeChildren: true,
      });
    }
  }, [accountingDispatch, user.mainBusiness?.id, perPage, filter]);

  //  Handle delete success
  useEffect(() => {
    if (deletedID) {
      // Close modal
      setShowConfirmModal(false);
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `delete-Expense-${deletedID}`,
        type: "success",
        title: "common.success",
        body: "expenses.notifications.deleteSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, deletedID]);

  // Handle inline edit success
  useEffect(() => {
    if (transactionID && isEditAmountModalOpen) {
      addLocalNotification(notificationDispatch, {
        id: `edit-transaction-${transactionID}`,
        type: "success",
        title: "common.success",
        body: "transactions.notifications.updateSuccess",
        showFull: true,
      });
      setSelectedExpense(null);
      setEditingValue("");
      setEditingError(null);
      setIsEditAmountModalOpen(false);
      clearAccountingTransaction(accountingDispatch);
    }
  }, [
    transactionID,
    isEditAmountModalOpen,
    notificationDispatch,
    accountingDispatch,
  ]);

  useEffect(() => {
    if (submitError) {
      setShowConfirmModal(false);
      // A failed amount edit stays open with what was typed and says why, so
      // it can be corrected and sent again.
      if (isEditAmountModalOpen) {
        setEditingError(getErrorMessages(submitError, t).join(" "));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitError]);

  useEffect(() => {
    if (
      submitError &&
      !showConfirmModal &&
      !isEditAmountModalOpen &&
      !isExpenseModalOpen &&
      !isInvoiceModalOpen &&
      !isInvoiceDetailModalOpen
    ) {
      setShowErrorModal(true);
    }
  }, [
    submitError,
    showConfirmModal,
    isEditAmountModalOpen,
    isExpenseModalOpen,
    isInvoiceModalOpen,
    isInvoiceDetailModalOpen,
  ]);

  useEffect(() => {
    if (user?.mainBusiness?.id) {
      onLoadStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountingDispatch, user?.mainBusiness?.id]);

  const onLoadStats = () => {
    requestGetExpenseStats(accountingDispatch, {
      businessID: user.mainBusiness.id,
      ...wibStatsMonths(),
    });
  };

  const onPageChange = (selectedPage) => {
    setPage(selectedPage);
    if (nextTokens[selectedPage] || selectedPage === 1) {
      requestListAccountingTransaction(accountingDispatch, {
        page: selectedPage,
        limit: perPage,
        businessID: user.mainBusiness.id,
        createdAt: {
          between: [
            wibThisMonth().startDate,
            wibThisMonth().endDate,
          ],
        },
        nextToken: nextTokens[selectedPage],
        filter,
        // Payment rows are dropped before the page is
        // counted, so a page can no longer come back empty.
        excludeChildren: true,
      });
    }
  };

  const onPerPageChange = (itemsPerPage) => {
    setPerPage(itemsPerPage);
  };

  // Every write entry point re-checks rather than trusting its caller: a row can
  // be locked from another tab between render and click.
  const handleDelete = (expense) => {
    if (isDateClosed(expense?.createdAt)) return;
    if (isStockAdjustmentJournal(expense)) return;
    setSelectedExpense(expense);
    setShowConfirmModal(true);
  };

  // The server refuses to delete a bill that still has payments, so the dialog
  // says so up front and its button stays disabled.
  const selectedPaymentCount = getPaymentCount(selectedExpense);

  const onDelete = () => {
    if (selectedPaymentCount > 0) return;
    requestDeleteAcctTransaction(accountingDispatch, {
      id: selectedExpense?.id,
    });
  };

  const getDeleteDescription = () => {
    if (!selectedExpense) return null;
    const date = dayjs(selectedExpense.createdAt)
      .locale(i18n.language)
      .format("D MMM YYYY");
    const amount = formatCurrency(selectedExpense.total ?? 0);
    const summary = selectedExpense.description
      ? t("expenses.modal.description.deleteItem", {
          description: selectedExpense.description,
          amount,
          date,
        })
      : t("expenses.modal.description.deleteItemNoDesc", { amount, date });
    if (selectedPaymentCount === 0) return summary;
    // Spans, not divs: ModalAlert renders this inside a <p>.
    return (
      <>
        {summary}
        <span className="mt-2 block font-medium text-red-600">
          {t("accounting.transactions.modal.description.hasPayments", {
            count: selectedPaymentCount,
          })}
        </span>
      </>
    );
  };

  const handleClose = (shouldRefresh) => {
    cancelAPIRequest(request);
    setIsExpenseModalOpen(false);
    setIsTransactionDetailModalOpen(false);
    setSelectedExpense(null);
    setIsInvoiceModalOpen(false);
    setIsInvoiceDetailModalOpen(false);
    setSelectedInvoice(null);
    if (shouldRefresh && user.mainBusiness?.id) {
      requestListAccountingTransaction(accountingDispatch, {
        page: 1,
        limit: perPage,
        businessID: user.mainBusiness.id,
        createdAt: {
          between: [
            wibThisMonth().startDate,
            wibThisMonth().endDate,
          ],
        },
        filter,
        // Payment rows are dropped before the page is
        // counted, so a page can no longer come back empty.
        excludeChildren: true,
      });
    }
  };

  const handleEdit = (expense) => {
    if (isDateClosed(expense?.createdAt)) return;
    if (isStockAdjustmentJournal(expense)) return;
    setSelectedExpense(expense);
    if (expense?.invoiceNumber) {
      setIsInvoiceModalOpen(true);
    } else {
      setIsExpenseModalOpen(true);
    }
  };

  const handleStartEdit = (transaction) => {
    if (isDateClosed(transaction?.createdAt)) return;
    if (isStockAdjustmentJournal(transaction)) return;
    setSelectedExpense(transaction);
    setEditingValue(transaction.total.toString());
    setEditingError(null);
    setIsEditAmountModalOpen(true);
  };

  const handleCancelEdit = () => {
    cancelAPIRequest(request);
    setSelectedExpense(null);
    setEditingValue("");
    setEditingError(null);
    setIsEditAmountModalOpen(false);
    // The failure was shown in this modal; don't raise it again on close.
    clearAcctTransactionError(accountingDispatch);
  };

  const handleSaveEdit = () => {
    if (isDateClosed(selectedExpense?.createdAt)) return;
    if (!editingValue || editingValue.trim() === "") {
      setEditingError("transactions.errors.amountRequired");
      return;
    }

    const newTotal = Number(editingValue);
    if (isNaN(newTotal) || newTotal <= 0) {
      setEditingError("accounting.transactions.modal.error.invalidAmount");
      return;
    }

    setEditingError(null);

    const input = {
      transaction_id: selectedExpense.id,
      business_id: user.mainBusiness.id,
      accounts:
        selectedExpense.accounts?.items?.map((account) => ({
          id: account.accountID,
          debit: account.debit > 0 ? newTotal : 0,
          credit: account.credit > 0 ? newTotal : 0,
        })) || [],
      type: selectedExpense.type,
      paid: selectedExpense.paid,
      created_at: new Date(selectedExpense.createdAt),
      description: selectedExpense.description,
      total: newTotal,
      payment_due_at: new Date(selectedExpense.paymentDueAt),
      partner_id: selectedExpense.partnerID || null,
    };

    requestUpdateAcctTransaction(accountingDispatch, { input });
  };

  const SkeletonStats = () => {
    return (
      <dl className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="px-4 py-5 shadow rounded-lg overflow-hidden sm:p-6 animate-pulse"
          >
            <dt className="text-sm font-medium text-gray-500 truncate">
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">
              <div className="h-8 bg-gray-300 rounded w-1/2"></div>
            </dd>
            <dd className="mt-2 flex items-center text-sm text-gray-600">
              <div className="h-4 bg-gray-300 rounded w-1/3"></div>
            </dd>
          </div>
        ))}
      </dl>
    );
  };


  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        {/* Header */}
        <AccountingPageHeader
          title="expenses.title"
          additionalActions={
            <button
              type="button"
              onClick={() => {
                setSelectedExpense(null);
                setIsInvoiceModalOpen(true);
              }}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full sm:w-auto"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              {t("bills.action.create")}
            </button>
          }
          primaryAction={{
            label: "expenses.header.title.add",
            onClick: () => setIsExpenseModalOpen(true),
            icon: <PlusIcon className="h-5 w-5" />,
          }}
        />

        {/* Stats */}
        <div className="mt-6 px-4 sm:px-6 lg:px-8">
          {expenseStatsLoading ? (
            <SkeletonStats />
          ) : expenseStatsError ? (
            <FormError
                error={expenseStatsError}
                title="common.errors.dataTitle"
                onRetry={onLoadStats}
              />
          ) : (
            <Stats stats={statsData} />
          )}
        </div>

        {/* Expense List */}
        <div className="px-4 py-5 sm:px-6 lg:px-8">
          <PeriodLockBanner
            businessID={user?.mainBusiness?.id}
            className="mb-4"
          />
          <TableHeader title={t("expenses.table.header")} />
          <Table
            isLoading={expenseLoading}
            error={expenseError}
            onRetry={() => onPageChange(page)}
            columns={[
              {
                header: "expenses.fields.date",
                accessor: (item) => <DateTimeCell value={item.createdAt} />,
                sortable: true,
                // The rows here are raw transactions, so the sortable field is
                // `createdAt`; "date" matched nothing and sorted nothing.
                sortKey: "createdAt",
              },
              {
                header: "expenses.fields.description",
                accessor: (item) => {
                  let descriptionText = "";
                  if (item.description) {
                    descriptionText = item.description;
                  } else if (item.invoiceNumber) {
                    descriptionText = `Invoice #${item.invoiceNumber}`;
                  } else {
                    descriptionText = t("expenses.noDescription");
                  }

                  return (
                    <div className="flex items-center max-w-xs whitespace-normal break-words">
                      {!item.description && !item.invoiceNumber ? (
                        <span className="text-gray-400 italic">
                          {descriptionText}
                        </span>
                      ) : (
                        <ExpandableDescription text={descriptionText} />
                      )}
                    </div>
                  );
                },
              },
              {
                header: "expenses.fields.type",
                accessor: (item) => t(convertToTitleCase(item.type)),
              },
              {
                header: "expenses.fields.amount",
                sortable: true,
                sortKey: "price",
                accessor: (item, index) => {
                  const originalTransaction = transactions[index];
                  // Clicking the amount opens an edit modal, so a locked row
                  // must lose the click target, hover, cursor, role and pencil
                  // together — the number itself stays readable.
                  const editable =
                    !originalTransaction?.invoiceNumber &&
                    !isStockAdjustmentJournal(originalTransaction) &&
                    !isDateClosed(item.createdAt);

                  return (
                    <div
                      className={`flex items-center justify-end px-2 py-1 rounded-md transition-colors duration-200 -mr-2 ${
                        editable ? "cursor-pointer group hover:bg-gray-100" : ""
                      }`}
                      onClick={() => {
                        if (editable) {
                          handleStartEdit(originalTransaction);
                        }
                      }}
                      role={editable ? "button" : undefined}
                      title={
                        editable
                          ? t("accounting.dashboard.transaction.clickToEdit")
                          : ""
                      }
                    >
                      <span className="font-medium">
                        Rp{item.total.toLocaleString()}
                      </span>
                      <div className="w-3.5 h-3.5 ml-2 flex-shrink-0">
                        {editable && (
                          <PencilIcon
                            className="w-full h-full text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                            aria-hidden="true"
                          />
                        )}
                      </div>
                    </div>
                  );
                },
                align: "right",
              },
              {
                header: "common.actions.label",
                accessor: (expense, index) => {
                  const viewExpense = () => {
                    if (expense?.invoiceNumber) {
                      setSelectedInvoice(transactions[index]);
                      setIsInvoiceDetailModalOpen(true);
                    } else {
                      setSelectedExpense(expense);
                      setIsTransactionDetailModalOpen(true);
                    }
                  };

                  // Cell swapped whole: TableActions changes shape between one
                  // action and several, so pruning the list would rewrite the
                  // control instead of just restricting it.
                  if (isDateClosed(expense.createdAt)) {
                    return (
                      <PeriodLockCell
                        className="justify-end"
                        onView={viewExpense}
                      />
                    );
                  }

                  // A stock-adjustment journal changes only with the stock (Atur Stok
                  // Produk); the server refuses it here.
                  if (isStockAdjustmentJournal(expense)) {
                    return (
                      <PeriodLockCell
                        className="justify-end"
                        label="accounting.stockAdjustmentJournal.lockedRow"
                        onView={viewExpense}
                      />
                    );
                  }

                  return (
                    <TableActions
                      position={
                        index === transactions.length - 1 ? "top" : "bottom"
                      }
                      actions={[
                        {
                          label: t("common.view"),
                          onClick: viewExpense,
                        },
                        {
                          label: t("common.edit"),
                          onClick: () => handleEdit(expense),
                        },
                        {
                          label: t("common.delete"),
                          onClick: () => handleDelete(expense),
                          variant: "danger",
                        },
                      ]}
                    />
                  );
                },
                align: "right",
              },
            ]}
            data={transactions}
          />
        </div>

        {/* Pagination */}
        <Pagination
          nextTokens={nextTokens}
          currentPage={page}
          isLoading={expenseLoading}
          perPage={perPage}
          onPageChange={onPageChange}
          onPerPageChange={onPerPageChange}
        />

        {/* Transaction Modal */}
        {isExpenseModalOpen && (
          <TransactionModal
            open={isExpenseModalOpen}
            onClose={handleClose}
            transaction={selectedExpense}
            transactionType="EXPENSE"
          />
        )}

        {/* Invoice Modal */}
        {isInvoiceModalOpen && (
          <InvoiceModal
            open={isInvoiceModalOpen}
            onClose={handleClose}
            invoice={selectedExpense}
            transactionType="EXPENSE_PAYABLE"
          />
        )}

        {/* Invoice Detail Modal */}
        {isInvoiceDetailModalOpen && (
          <InvoiceDetailModal
            open={isInvoiceDetailModalOpen}
            invoice={selectedInvoice}
            onClose={() => {
              setIsInvoiceDetailModalOpen(false);
              setSelectedInvoice(null);
            }}
            onEdit={(invoice) => {
              setIsInvoiceDetailModalOpen(false);
              setSelectedExpense(invoice);
              setIsInvoiceModalOpen(true);
            }}
          />
        )}

        {isTransactionDetailModalOpen && (
          <TransactionDetailModal
            open={isTransactionDetailModalOpen}
            onClose={() => {
              setIsTransactionDetailModalOpen(false);
              setSelectedExpense(null);
            }}
            onEdit={(expense) => {
              setIsTransactionDetailModalOpen(false);
              handleEdit(expense);
            }}
            transaction={selectedExpense}
          />
        )}

        {/* Delete Modal */}
        <ModalAlert
          open={showConfirmModal}
          setOpen={(open) => {
            setShowConfirmModal(open);
          }}
          title="expenses.modal.title.delete"
          description="expenses.modal.description.delete"
          descriptionNode={getDeleteDescription()}
          confirmButtonText="common.delete"
          cancelButtonText="Cancel"
          loadingButton={submitLoading}
          disabledButton={submitLoading || selectedPaymentCount > 0}
          onConfirm={onDelete}
          onCancel={() => {
            cancelAPIRequest(request);
            setShowConfirmModal(false);
            setSelectedExpense(null);
          }}
        />

        {/* Error Modal */}
        {submitError && (
          <ErrorDialog
            open={showErrorModal}
            message={getHasPaymentsMessage(submitError)}
            error={submitError}
            onClose={() => {
              setShowErrorModal(false);
              clearAcctTransactionError(accountingDispatch);
            }}
          />
        )}

        {/* Edit Amount Modal */}
        <Modal
          open={isEditAmountModalOpen}
          onClose={handleCancelEdit}
          title="accounting.dashboard.transaction.clickToEdit"
          footer={
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                onClick={handleCancelEdit}
                variant="secondary"
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="button"
                onClick={handleSaveEdit}
                disabled={submitLoading}
                variant="primary"
              >
                {t("common.save")}
              </Button>
            </div>
          }
        >
          <FormInput
            label={t("transactions.fields.amount")}
            value={editingValue}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !submitLoading) {
                handleSaveEdit();
              }
            }}
            // The plain number is kept, as in TransactionModal: fed back as
            // "3.000", the formatted text had its "." read as a decimal point.
            numberFormat={{
              thousandSeparator: ".",
              decimalSeparator: ",",
              allowedDecimalSeparators: [","],
              valueIsNumericString: true,
              onValueChange: ({ value }) => {
                setEditingValue(value);
                if (editingError) setEditingError(null);
              },
            }}
            prefix="Rp"
            error={editingError}
            disabled={submitLoading}
            onClear={() => setEditingValue("")}
            autoFocus
          />
        </Modal>
      </main>
    </AccountingWrapper>
  );
};

export default AccountingExpenses;
